import { MessageSquare } from 'lucide-react';
import { useState } from 'react';
import type { ShareRow } from '#shared/types';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Identifier } from '#client/components/ui/Identifier';
import { useCan } from '#client/hooks/useCan';
import { useMutation } from '#client/hooks/useMutation';
import { formatDateTime } from '#client/lib/format';

import { useToast } from '#client/providers/ToastProvider';
import { sendShare } from './share.service';
import { retryColumn, shareHistoryColumns } from './share-history-columns';

interface ShareHistoryCardProps {
  patientId: number;
  shares: ShareRow[];
  onRetried: () => void;
}

/**
 * Every attempt to put this visit's reports on a phone.
 *
 * Append-only, and shown that way: a retry is a new row rather than an edit to
 * the old one, so the question somebody actually asks later — "how many times
 * did we message this patient, and did it arrive?" — has an answer on the
 * screen rather than in a database.
 */
export function ShareHistoryCard({ patientId, shares, onRetried }: ShareHistoryCardProps) {
  const can = useCan();
  const toast = useToast();
  const retryRequest = useMutation('That retry did not go through.');
  const [retryingId, setRetryingId] = useState<number | null>(null);

  async function handleRetry(share: ShareRow) {
    setRetryingId(share.id);
    const outcome = await retryRequest.run(() =>
      sendShare(patientId, { reportIds: share.report_ids, confirmResend: true }),
    );
    setRetryingId(null);
    if (outcome.ok) onRetried();
    else toast.error(outcome.message);
  }

  const columns = [
    ...shareHistoryColumns,
    retryColumn({
      canRetry: can('whatsapp.share'),
      retryingId,
      onRetry: (share) => void handleRetry(share),
    }),
  ];

  return (
    <Card title={`WhatsApp (${shares.length})`} flush>
      <DataTable
        caption="WhatsApp sends for this patient"
        columns={columns}
        rows={shares}
        rowKey={(row) => row.id}
        stickyHeader={false}
        emptyState={
          <EmptyState
            icon={<MessageSquare />}
            title="Not sent on WhatsApp"
            body="When a finalized report is sent, every attempt is recorded here with the number it went to."
          />
        }
      />

      <FailureNotes shares={shares} />
    </Card>
  );
}

function FailureNotes({ shares }: { shares: ShareRow[] }) {
  return shares
    .filter((share) => share.status === 'failed' && share.error_message)
    .map((share) => (
      <p key={share.id} className="c-field__help">
        <strong>{formatDateTime(share.created_at)}:</strong> {share.error_message}{' '}
        {share.fbtrace_id && (
          // Meta support asks for this first. Copyable, because reading a
          // trace id down a phone line is how a digit gets lost.
          <Identifier value={share.fbtrace_id} copyable />
        )}
      </p>
    ));
}
